import { useTranslation } from 'react-i18next';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import { IContractTemplateParagraph } from '@clickersphere/core/services/ContractTemplateService';
import { Button } from '../../../Button';
import Checkbox from '../../../Checkbox';
import { Input } from '../../../Input';
import Modal from '../../../Modal';
import Select from '../../../Select';
import { Tabs } from '../../../Tabs';
import {
  IAnchorSelection,
  ICreateAnchorPayload,
} from '../useTemplateModalController';
import { IEditableContractVariable } from '../VariableMappingEditor';
import {
  ITagReviewContext,
  useTagVariablePopoverController,
} from './useTagVariablePopoverController';

interface ITagVariablePopoverProps {
  selection: IAnchorSelection;
  variables: IEditableContractVariable[];
  paragraphs: IContractTemplateParagraph[];
  review?: ITagReviewContext;
  onConfirm: (payload: ICreateAnchorPayload) => void;
  onRemoveAnchor?: () => void;
  onClose: () => void;
}

function NewVariableForm({
  label,
  labelError,
  source,
  required,
  derivedKey,
  sourceOptions,
  onLabelChange,
  onSourceChange,
  onRequiredChange,
}: {
  label: string;
  labelError?: string;
  source: ContractVariableSourceEnum;
  required: boolean;
  derivedKey: string;
  sourceOptions: { value: string; text: string }[];
  onLabelChange: (value: string) => void;
  onSourceChange: (value: string) => void;
  onRequiredChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <Input
        type="text"
        name="new-variable-label"
        placeholder={t('contract.templates.editor.popover.labelField')}
        value={label}
        error={labelError}
        onChange={(event) => onLabelChange(event.target.value)}
      />
      <Select
        placeholder={t('contract.templates.editor.popover.sourceField')}
        options={sourceOptions}
        value={source}
        onChange={onSourceChange}
      />
      <label
        htmlFor="new-variable-required"
        className="text-text-primary flex h-10 cursor-pointer items-center gap-2 text-sm"
      >
        <Checkbox
          id="new-variable-required"
          checked={required}
          onCheckedChange={onRequiredChange}
        />
        {t('contract.templates.editor.popover.requiredField')}
      </label>
      <Input
        type="text"
        name="new-variable-key"
        placeholder={t('contract.templates.editor.popover.keyField')}
        value={derivedKey}
        readOnly
      />
    </div>
  );
}

export default function TagVariablePopover({
  selection,
  variables,
  paragraphs,
  review,
  onConfirm,
  onRemoveAnchor,
  onClose,
}: ITagVariablePopoverProps) {
  const {
    t,
    isReview,
    reviewStep,
    showTagForm,
    hasExisting,
    mode,
    targetRowId,
    label,
    labelError,
    source,
    required,
    derivedKey,
    applyToOthers,
    otherOccurrencesCount,
    sourceOptions,
    variableOptions,
    setMode,
    setTargetRowId,
    setRequired,
    setApplyToOthers,
    handleLabelChange,
    handleSourceChange,
    handleStartChange,
    handleConfirm,
  } = useTagVariablePopoverController({
    selection,
    variables,
    paragraphs,
    review,
    onConfirm,
  });

  return (
    <Modal
      variant="sheet"
      className="z-40 flex max-h-[90vh] max-w-md flex-col gap-4 overflow-y-auto"
      title={
        isReview
          ? t('contract.templates.editor.popover.reviewTitle')
          : t('contract.templates.editor.popover.title')
      }
      onClose={onClose}
    >
      <div className="bg-surface-input flex flex-col gap-1 rounded-lg p-3">
        <span className="text-text-tertiary text-xs">
          {t('contract.templates.editor.popover.context')}
        </span>
        <span className="text-text-primary text-sm font-medium break-words">
          “{selection.matchText}”
        </span>
      </div>

      {isReview && reviewStep === 'menu' && (
        <div className="flex flex-col gap-4">
          <p className="text-text-secondary text-sm">
            {t('contract.templates.editor.popover.linkedTo', {
              label: review?.varLabel ?? '',
            })}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              className="w-full"
              color="danger"
              labelPath="contract.templates.editor.popover.removeAnchor"
              onClick={onRemoveAnchor}
            />
            <Button
              className="w-full"
              color="outline"
              labelPath="contract.templates.editor.popover.changeVariable"
              onClick={handleStartChange}
            />
          </div>
        </div>
      )}

      {showTagForm && (
        <>
          {hasExisting ? (
            <Tabs.Root
              value={mode}
              onValueChange={(value) => setMode(value as typeof mode)}
            >
              <Tabs.List
                aria-label={t('contract.templates.editor.popover.title')}
              >
                <Tabs.Trigger value="existing" className="flex-1">
                  {t('contract.templates.editor.popover.linkExisting')}
                </Tabs.Trigger>
                <Tabs.Trigger value="new" className="flex-1">
                  {t('contract.templates.editor.popover.createNew')}
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="existing" className="pt-4">
                <Select
                  placeholder={t(
                    'contract.templates.editor.popover.existingField',
                  )}
                  options={variableOptions}
                  value={targetRowId}
                  onChange={setTargetRowId}
                />
              </Tabs.Content>

              <Tabs.Content value="new" className="pt-4">
                <NewVariableForm
                  label={label}
                  labelError={labelError}
                  source={source}
                  required={required}
                  derivedKey={derivedKey}
                  sourceOptions={sourceOptions}
                  onLabelChange={handleLabelChange}
                  onSourceChange={handleSourceChange}
                  onRequiredChange={setRequired}
                />
              </Tabs.Content>
            </Tabs.Root>
          ) : (
            <NewVariableForm
              label={label}
              labelError={labelError}
              source={source}
              required={required}
              derivedKey={derivedKey}
              sourceOptions={sourceOptions}
              onLabelChange={handleLabelChange}
              onSourceChange={handleSourceChange}
              onRequiredChange={setRequired}
            />
          )}

          {otherOccurrencesCount > 0 && (
            <label
              htmlFor="apply-to-others"
              className="text-text-primary flex min-h-10 cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                id="apply-to-others"
                checked={applyToOthers}
                onCheckedChange={setApplyToOthers}
              />
              {t('contract.templates.editor.popover.applyToOthers', {
                count: otherOccurrencesCount,
              })}
            </label>
          )}

          <footer className="mt-1 flex flex-col gap-3 sm:flex-row">
            <Button
              className="w-full sm:w-40"
              color="ghost"
              labelPath="contract.templates.editor.popover.cancel"
              onClick={onClose}
            />
            <Button
              className="w-full"
              labelPath="contract.templates.editor.popover.confirm"
              onClick={handleConfirm}
            />
          </footer>
        </>
      )}
    </Modal>
  );
}
